/**
 * SEO hub pages: sample 3/5/7-day AI itineraries + editorial framing (see GuideArticle).
 */
import { ITINERARY_HUB_HOSTS, itineraryHubSlug, ITINERARY_HUB_HERO_BY_SLUG } from './itineraryHubMeta';

const DEFAULT_COVER =
  'https://lh3.googleusercontent.com/aida-public/AB6AXuBjYMa1iFm6OOj4aAcJFH_gkwTkFpUfH83yyHSkf75bPGwyZd8_9plR8myBsUyCYdmvcEAPMpUQ4QwzDlBdx85SFPM9IvtlcLMMkw15kByyHhajwHozjrTrf0FcEfNIyflK_L67qArYavtFu52Y7f2RYPNEZtr7boXayxnHzCiOMRTwsJxvB4GQ80ccmQ-XkUEo_hI83JUuoL-So4KWuzSu7pHPA_QGpcn25pfga_ATibNzNCYUwKTpphYRJGSiyTP-sKhCodQBuPw';

const STADIUM_STOCK =
  'https://images.unsplash.com/photo-1574629810360-7efbbe195018?auto=format&fit=crop&w=2000&q=80';

function buildArticle(host) {
  const {
    guideSlug,
    metroLabel,
    stadiumVenue,
    venueMunicipality,
    stadiumSlug,
    country,
    editorialCityGuideSlug,
  } = host;

  const hero = ITINERARY_HUB_HERO_BY_SLUG[guideSlug] || { src: DEFAULT_COVER, alt: metroLabel };
  const slug = itineraryHubSlug(guideSlug);
  const title = `${metroLabel} World Cup 2026 Itinerary: 3, 5 & 7 Day Sample Plans (${stadiumVenue})`;
  const description = `FIFA World Cup 2026 in ${metroLabel}: sample AI-built 3-, 5-, and 7-day itineraries anchored near ${stadiumVenue} (${venueMunicipality}), transit and food tips, and a link to customize your trip.`;
  const h1 = `${metroLabel}: your 3, 5 & 7-day World Cup plan near ${stadiumVenue}`;

  const relatedGuideSlugs = [
    ...(editorialCityGuideSlug ? [editorialCityGuideSlug] : []),
    'first-time-world-cup-guide',
    'budget-world-cup-2026',
  ].filter((v, i, a) => a.indexOf(v) === i);

  const introStadium = `The 2026 tournament places matches at ${stadiumVenue} in ${venueMunicipality}, ${country}. Fans usually stay across the wider ${metroLabel} area—so these day plans mix stadium logistics with metro sightseeing, food, and realistic transport buffers.`;

  const sidebarTips = [
    `Compare 3-, 5-, and 7-day tabs below before locking hotels in ${metroLabel}.`,
    `Treat ${venueMunicipality} as the map anchor for ${stadiumVenue}, even if you sleep elsewhere.`,
    'Screenshot official transit detours for match night; operations can differ from a normal weekend.',
  ];

  const sidebarWarnings = [
    'Stacking tight reservations on your match-day afternoon',
    'Assuming last-minute rideshare at the main gate after full time',
    'Ignoring FIFA and stadium bag rules until arrival day',
  ];

  return {
    slug,
    title,
    description,
    h1,
    itineraryGuideDisplayName: metroLabel,
    heroImage: hero.src,
    heroImageAlt: hero.alt,
    heroBadge: 'Itinerary hub · 3 / 5 / 7 days',
    heroSubtitle: `Sample AI itineraries for ${metroLabel}—switch tabs, then open the planner to personalize dates, budget, and match day.`,
    heroAccent: { word: '3, 5 & 7-day', position: 'after' },
    updated: '2026-04-11',
    sidebarTips,
    sidebarWarnings,
    listing: {
      displayTag: 'Itinerary hub',
      tagTone: 'primary',
      coverImage: hero.src,
      readMinutes: 11,
      filterIds: ['all', 'itinerary-hub'],
    },
    sections: [
      {
        title: `Why we built a dedicated itinerary page for ${metroLabel}`,
        paragraphs: [
          `Search engines—and travelers—do best when “${metroLabel} World Cup itinerary” lives on one bookmarkable URL with clear day-by-day structure. This hub pairs short editorial context with the same sample AI itineraries we generate for the venue municipality (${venueMunicipality}), so you can compare 3-, 5-, and 7-day pacing before opening the interactive planner.`,
          introStadium,
        ],
      },
      {
        type: 'feature_grid',
        cards: [
          {
            title: 'Pick your trip length',
            text: 'Use 3 days for a stadium-first weekend, 5 for one match plus city time, 7 for slower pacing or knockout buffer.',
            accent: 'primary',
          },
          {
            title: 'Stay flexible on match night',
            text: `Kickoff, security, and exit flows at ${stadiumVenue} can stretch your evening—build margin into dinner and transit.`,
            accent: 'secondary',
          },
        ],
      },
      {
        title: 'How to use the sample plans below',
        paragraphs: [
          'Pick a trip length tab (3, 5, or 7 days). Skim morning, afternoon, and evening blocks, then read food picks, football experiences, and travel tips in the cards above the day grid. Treat times as planning anchors, not fixed appointments—kickoffs, security, and transit can all add delay.',
          'When you are ready to personalize (different match day, budget, or interests), use the planner in the sidebar; it pre-fills the venue-focused city and duration you selected.',
        ],
      },
      {
        type: 'callout',
        variant: 'primary',
        title: `Pro tip: anchor maps to ${venueMunicipality}`,
        text: `Ticketing and transit directions often reference ${venueMunicipality} and ${stadiumVenue}—keep that pin saved even if your hotel brand says “${metroLabel}.”`,
      },
      {
        type: 'numbered_points',
        title: `Smarter days in ${metroLabel}`,
        intro: `Three habits that keep ${metroLabel} trips calmer during the World Cup:`,
        items: [
          { title: 'Leave early on stadium day', text: 'Aim to arrive with time for security, photos, and food—crowds are part of the show.' },
          { title: 'Plan a post-match meetup', text: 'Pick a landmark away from the densest gate cluster so your group can regroup without phone chaos.' },
          { title: 'Re-check official apps', text: 'Shuttle routes and fan fests can change by window—trust FIFA and host-city channels over old screenshots.' },
        ],
      },
      {
        type: 'hero_image_section',
        image: STADIUM_STOCK,
        alt: `Soccer stadium atmosphere — plan your approach to ${stadiumVenue}`,
        title: `${stadiumVenue} game plan`,
        overlayTitle: 'After the whistle',
        overlayText: `Big nights mean big exits. Build a buffer for ${stadiumVenue}: water, meetup point, and a calm path to transit or a post-match meal while crowds thin.`,
      },
      {
        type: 'image_grid',
        items: [
          {
            title: `${metroLabel} base`,
            text: `Use these days to explore ${metroLabel} neighborhoods, food halls, and fan energy—then let match day anchor the trip.`,
            image: hero.src,
            imageAlt: hero.alt,
            tag: 'Host metro',
            tagTone: 'primary',
          },
          {
            title: 'Venue focus',
            text: `${stadiumVenue} is the logistics spine—transit passes, parking, and walking routes should assume tournament crowds.`,
            image: STADIUM_STOCK,
            imageAlt: `Stadium planning for ${stadiumVenue}`,
            tag: 'Match day',
            tagTone: 'tertiary',
          },
        ],
      },
      { type: 'prebuilt_itinerary', prebuiltBundleKey: guideSlug },
      {
        title: 'Verify before you book',
        paragraphs: [
          'World Cup operations evolve: fan fests, shuttles, and stadium security can change as the tournament nears. Use FIFA, the local host committee, and official stadium communications as the final word on dates, bags, and transit.',
          `For venue-specific seating and arrival strategy, also open the ${stadiumVenue} stadium page from the related links below.`,
        ],
      },
    ],
    faq: [
      {
        question: `Are these itineraries official FIFA or ${metroLabel} host content?`,
        answer:
          'No. They are fan-planning aids built with AI and editorial review. Always confirm schedules, tickets, and safety guidance with FIFA and official city or stadium sources.',
      },
      {
        question: `Why does the planner use “${venueMunicipality}” for some maps?`,
        answer: `The venue sits in ${venueMunicipality}. The AI uses that anchor for realistic distances to ${stadiumVenue}; your hotels may be elsewhere in ${metroLabel}, so adjust times in the planner.`,
      },
      {
        question: 'Which length should I choose: 3, 5, or 7 days?',
        answer:
          'Use 3 days for a tight stadium-focused long weekend, 5 days for one match plus sightseeing, and 7 days if you want slower pacing, day trips, or buffer around knockout extras.',
      },
      {
        question: `How do I plan match day at ${stadiumVenue}?`,
        answer:
          'Pick the tab that includes a mid-trip “match day,” then read travel tips for transit passes and stadium approach. Arrive earlier than feels comfortable on big nights and avoid tight dinner reservations before kickoff.',
      },
    ],
    relatedGuideSlugs,
    relatedCitySlugs: [guideSlug],
    relatedStadiumSlugs: [stadiumSlug],
    plannerCta: {
      city: venueMunicipality,
      days: 5,
      matchDay: 3,
      budget: 'medium',
      transport: 'public_transport',
    },
  };
}

export const ITINERARY_HUB_ARTICLES = ITINERARY_HUB_HOSTS.map(buildArticle);
